import { useEffect, useRef, useState } from 'react';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { useHistory, useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useIntl } from 'react-intl';

import { api } from '~/api';
import { authService } from '~/services/auth-service';
import { getEnv, parseSearch } from '~/utils';

import type { TokenMode } from './TokenStep';

const validateEmail = (email: string) => /\S+@\S+\.\S+/.test(email);
const clientId = getEnv(import.meta.env.VITE_APP_WEB_CLIENT_ID);

export const useSignInFlow = () => {
  const { formatMessage } = useIntl();
  const isInitialLoading = useStoreState((state: any) => state.auth.loading);
  const setLoading = useStoreActions((actions: any) => actions.auth.setLoading);
  const checkAuth = useStoreActions((actions: any) => actions.auth.checkAuth);

  const history = useHistory();
  const location = useLocation<{ from?: string }>();
  const search = parseSearch(history.location?.search);
  const camefrom = location.state?.from ?? '/';

  const codeInputRef = useRef<HTMLInputElement>(null);

  const [email, setEmail] = useState('');
  const [token, setToken] = useState(search.token || '');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [codeResent, setCodeResent] = useState(false);
  // Sticky once a code is sent: keeps us on the code step while a Resend
  // re-runs signInMutation (which would otherwise flip isSuccess back to false).
  const [emailSent, setEmailSent] = useState(false);

  // The deep link the user was bounced from; sent to the backend so it can be
  // stored on the auth token and redirected to after auth (incl. magic links
  // opened on another device). '/' means "no deep link" — don't send it.
  const redirectTo = camefrom !== '/' ? camefrom : undefined;

  const signInMutation = useMutation({
    mutationFn: ({ email, solution }: { email: string; solution?: string }) =>
      api.signIn(email, solution, redirectTo),
    onSuccess: () => {
      setToken('');
      setValidationError(null);
      setEmailSent(true);
    },
  });

  const authMutation = useMutation({
    // email scopes the non-unique 5-digit short code server-side; omitted for
    // the full-token magic link, which resolves unscoped.
    mutationFn: ({ token, email }: { token: string; email?: string }) =>
      api.authenticate(token, clientId, email),
    onSuccess: async (response) => {
      const { token_type, user_id, access_token, expires_in } = response;
      // checkAuth can throw (e.g. user with no solutions); a throw here is not
      // routed into the mutation's isError, so recover manually: release the
      // loader and reset the mutation so isSuccess clears and the URL magic-link
      // path falls back to the "expired / start over" state instead of hanging
      // on "Redirecting...".
      try {
        await checkAuth({
          token: { user_id, access_token, token_type, expires_in },
          target: response?.target ?? {},
          redirect: true,
          camefrom,
        });
      } catch {
        setLoading(false);
        authMutation.reset();
      }
    },
    onError: () => setLoading(false),
  });

  const submitEmail = () => {
    if (!validateEmail(email)) {
      setValidationError(
        formatMessage({
          id: 'sign-in-form-email-error',
          defaultMessage: 'Please provide a valid email address',
        })
      );
      return;
    }
    setValidationError(null);
    signInMutation.mutate({ email, solution: search.solution });
  };

  const submitToken = (code = token) =>
    authMutation.mutate({ token: code, email });

  const handleTokenChange = (value: string) => {
    setToken(value);
    setCodeResent(false);
    if (authMutation.isError) authMutation.reset();
  };

  const resendCode = () => {
    setCodeResent(false);
    setToken('');
    authMutation.reset();
    signInMutation.mutate(
      { email, solution: search.solution },
      {
        onSuccess: () => {
          setCodeResent(true);
          // Clicking Resend moved focus to the button, leaving the OTP input
          // visually focused but not keyboard-ready. Restore focus so the user
          // can type the new code without clicking back in.
          codeInputRef.current?.focus();
        },
      }
    );
  };

  const stepBack = () => {
    setValidationError(null);
    setEmailSent(false);
    signInMutation.reset();
    authMutation.reset();
  };

  const startOver = () => {
    history.push('/signin');
    stepBack();
  };

  useEffect(() => {
    if (token) {
      authMutation.mutate({ token });
    } else if (location.hash !== '#signout' && authService.loadToken()) {
      // On failure (e.g. network drop, FRAICHE-A) release the loader so the
      // sign-in form shows instead of leaking an unhandled rejection.
      checkAuth({ camefrom, urlSolution: search.solution_id }).catch(() =>
        setLoading(false)
      );
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emailError =
    validationError ??
    (signInMutation.isError
      ? formatMessage({
          id: 'sign-in-form-unknown-error',
          defaultMessage:
            'Something went wrong. Please refresh the page and try again.',
        })
      : null);

  // A server-side rejection (e.g. "code invalid or expired") shows friendly
  // copy with an inline resend link (rendered in TokenStep); only unexpected /
  // network errors fall back to the generic refresh message.
  const invalidCode =
    authMutation.isError &&
    !!(authMutation.error as any)?.response?.data?.error_description;

  const tokenError =
    authMutation.isError && !invalidCode
      ? formatMessage({
          id: 'sign-in-form-callback-error',
          defaultMessage:
            'Something went wrong. Please refresh the page and try again.',
        })
      : null;

  const tokenMode: TokenMode = email
    ? 'inbox'
    : authMutation.isSuccess
      ? 'redirecting'
      : 'expired';

  const step: 1 | 2 = emailSent || !!search.token ? 2 : 1;

  return {
    step,
    showInitialLoader: !!search.token && isInitialLoading,
    solution: search.solution,
    emailStep: {
      email,
      onEmailChange: setEmail,
      onSubmit: submitEmail,
      isPending: signInMutation.isPending,
      error: emailError,
    },
    tokenStep: {
      mode: tokenMode,
      email,
      token,
      inputRef: codeInputRef,
      onTokenChange: handleTokenChange,
      onSubmit: submitToken,
      onResend: resendCode,
      onStartOver: startOver,
      onUseDifferentEmail: stepBack,
      resendPending: signInMutation.isPending,
      codeResent,
      isPending: authMutation.isPending || authMutation.isSuccess,
      error: tokenError,
      invalidCode,
    },
  };
};
